"use client";

import { ArrowRight, Building2, Code as Code2, FileText, Layers, ChartLine as LineChart, MapPin, Network, Search, Star, Target } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function Localseoservicesgrid() {
  const services = [{
    number: "PILLAR 01 — KW",
    title: "Bedford-Specific Keyword Research.",
    description: "I uncover high-intent search terms used by Bedford customers, mapping local search intent to drive qualified enquiries directly to your business.",
    tags: ["Bedford Intent", "Search Volume", "Relevance"],
    icon: Search,
    link: "/ServicesPage"
  }, {
    number: "PILLAR 02 — GBP",
    title: "Google Business Profile Optimisation.",
    description: "I fine-tune your Google Business Profile to maximise visibility across Google Maps and the Bedford local 3-pack.",
    tags: ["Google Maps", "Local 3-Pack", "NAP Consistency"],
    icon: MapPin,
    link: "/ServicesPage"
  }, {
    number: "PILLAR 03 — TECH",
    title: "Technical SEO.",
    description: "I resolve crawl anomalies, indexing barriers, and Core Web Vitals issues so search engines can index your local website effortlessly.",
    tags: ["Crawlability", "Site Speed", "Schema Markup"],
    icon: Code2,
    link: "/technical-seo"
  }, {
    number: "PILLAR 04 — ONPAGE",
    title: "On-Page SEO.",
    description: "I optimise your page titles, meta descriptions, headings, and copy to establish crystal-clear local relevance for Bedford search queries.",
    tags: ["Meta Tags", "Header Hierarchy", "Relevance"],
    icon: FileText,
    link: "/technical-seo"
  }, {
    number: "PILLAR 05 — CONTENT",
    title: "Local Content Strategy.",
    description: "I craft authoritative, locally focused content that answers the specific needs of Bedford and Bedfordshire buyers.",
    tags: ["Local Authority", "Editorial Content", "User Intent"],
    icon: Layers,
    link: "/local-seo"
  }, {
    number: "PILLAR 06 — LINKS",
    title: "Internal Linking.",
    description: "I build logical site architecture and contextual internal links that distribute authority directly to your most critical local service pages.",
    tags: ["Site Architecture", "Anchor Text", "Equity Flow"],
    icon: Network,
    link: "/ServicesPage"
  }, {
    number: "PILLAR 07 — CITATIONS",
    title: "Local Citations.",
    description: "I secure consistent NAP listings across reputable UK and Bedford business directories to reinforce your geographic authority.",
    tags: ["UK Directories", "NAP Consistency", "Local Trust"],
    icon: Building2,
    link: "/local-seo"
  }, {
    number: "PILLAR 08 — REVIEWS",
    title: "Review Strategy.",
    description: "I help you build an ethical review generation framework that strengthens customer trust and lifts local map pack rankings.",
    tags: ["Social Proof", "Reputation", "Customer Trust"],
    icon: Star,
    link: "/ServicesPage"
  }, {
    number: "PILLAR 09 — CRO",
    title: "Conversion Tracking.",
    description: "I configure precise analytics and conversion tracking to measure every phone call, form submission, and lead generated from organic search.",
    tags: ["GA4 Tracking", "Lead Capture", "Event Measurement"],
    icon: LineChart,
    link: "/ServicesPage"
  }, {
    number: "PILLAR 10 — COMP",
    title: "Competitor Research.",
    description: "I analyse top-ranking Bedford competitors to identify keyword gaps, content deficits, and backlink opportunities you can capitalise on.",
    tags: ["Market Analysis", "Gap Analysis", "Benchmarking"],
    icon: Target,
    link: "/ServicesPage"
  }];
  return <section data-section-id="23134" id="localseoservicesgrid" data-nav="light" className="bg-background py-[clamp(6rem,10vw,10rem)]">
      <div className="mx-auto max-w-[1440px] px-[clamp(1.5rem,4vw,4.5rem)]">
        <div className="h-px w-full bg-border" />

        <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-8">
          <div className="md:col-span-3">
            <span className="font-sans text-xs font-medium uppercase text-muted-foreground" style={{
            letterSpacing: "0.16em"
          }}>
              BEDFORD SEO — 10 PILLARS
            </span>
          </div>

          <div className="md:col-span-9">
            <h2 className="font-serif text-foreground" style={{
            fontSize: "clamp(1.75rem, 2.75vw, 2.375rem)",
            lineHeight: "0.98",
            letterSpacing: "-0.028em",
            fontWeight: 400
          }}>
              Why My Bedford{" "}
              <br className="hidden sm:block" />
              SEO Services{" "}
              <br className="hidden sm:block" />
              <span className="relative inline-block italic">
                <span className="relative z-10">Work</span>
                <svg aria-hidden="true" className="absolute left-0 right-0 -bottom-2 w-full" viewBox="0 0 300 18" preserveAspectRatio="none" style={{
                height: "0.4em"
              }}>
                  <path d="M2,12 C60,4 120,15 180,8 C220,4 260,12 298,7" stroke="var(--accent)" strokeWidth="8" strokeLinecap="round" fill="none" opacity="0.55" />
                </svg>
              </span>
              <span className="text-foreground">.</span>
            </h2>

            <p className="mt-12 max-w-2xl font-sans text-foreground" style={{
            fontSize: "clamp(1.0625rem, 1.25vw, 1.1875rem)",
            lineHeight: "1.55",
            letterSpacing: "-0.005em"
          }}>
              I deliver tailored, transparent local SEO strategies built around the Bedford market. Every action is designed to build sustainable rankings, drive qualified local enquiries, and turn organic search into a dependable growth channel for your business.
            </p>

            <div className="mt-10">
              <Button asChild variant="outline" className="h-14 rounded-lg border-[1.5px] border-primary bg-transparent px-8 text-primary hover:bg-primary hover:text-primary-foreground">
                <Link to="/ContactPage">
                  <span className="font-sans text-[17px] font-medium">
                    Book a Free Consultation
                  </span>
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-24 grid grid-cols-1 gap-8 md:grid-cols-2">
          {services.map((service, i) => {
          const isNavy = i % 2 === 1;
          const Icon = service.icon;
          return <article key={i} data-index={i} className={["group relative flex flex-col p-[clamp(1.75rem,2.5vw,2.75rem)]", "rounded-3xl transition-all duration-300", isNavy ? "bg-primary text-primary-foreground" : "bg-card text-card-foreground"].join(" ")}>
                <div className="transition-transform duration-300 ease-out group-hover:-translate-y-1">
                  <Icon className={["h-6 w-6", isNavy ? "text-primary-foreground" : "text-primary"].join(" ")} strokeWidth={1.25} />

                  <div className="mt-6">
                    <span className={["font-sans text-xs font-medium uppercase", isNavy ? "text-primary-foreground/70" : "text-muted-foreground"].join(" ")} style={{
                  letterSpacing: "0.16em"
                }}>
                      {service.number}
                    </span>
                  </div>

                  <h3 className={["mt-5 font-serif", isNavy ? "text-primary-foreground" : "text-foreground"].join(" ")} style={{
                fontSize: "clamp(1.375rem, 1.75vw, 1.625rem)",
                lineHeight: "1.08",
                letterSpacing: "-0.02em",
                fontWeight: 400
              }}>
                    {service.title}
                  </h3>
                  <div className={["mt-3 h-px w-0 transition-all duration-500 ease-out group-hover:w-16", isNavy ? "bg-accent" : "bg-primary"].join(" ")} />

                  <p className={["mt-5 font-sans", isNavy ? "text-primary-foreground/85" : "text-foreground/80"].join(" ")} style={{
                fontSize: "clamp(1.0625rem, 1.15vw, 1.125rem)",
                lineHeight: "1.6"
              }}>
                    {service.description}
                  </p>

                  <div className="mt-8 flex flex-wrap gap-2">
                    {service.tags.map((tag, j) => <span key={j} data-index={j} className={["inline-flex items-center rounded-full border px-4 py-2 font-sans text-xs font-medium uppercase", isNavy ? "border-primary-foreground/40 text-primary-foreground" : "border-primary/20 text-primary"].join(" ")} style={{
                  letterSpacing: "0.16em"
                }}>
                        {tag}
                      </span>)}
                  </div>

                  <div className="mt-10">
                    <Link to={service.link} className={["group/link relative inline-flex items-center gap-2 font-sans text-[17px] font-medium", isNavy ? "text-primary-foreground" : "text-primary"].join(" ")}>
                      <span className="relative">
                        Learn more
                        <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-accent transition-all duration-300 ease-out group-hover/link:w-full" />
                      </span>
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </article>;
        })}
        </div>
      </div>
    </section>;
}